import { Descriptions, Table, Tabs } from 'antd';
import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';
import withStyles from 'react-jss';
import { useDispatch, useSelector } from 'react-redux';
import startCase from 'lodash/startCase';
import moment from 'moment';

import {
  clearDepartmentTrackingOfJobchart,
  getDepartmentTrackingOfJobchart,
  getRPOReport,
  clearRPOReport,
  getMRR,
  getOutwardReport,
  clearMRR,
  clearOutwardReport,
  getIssuedItemsOfJobchart,
  clearIssuedItemsOfJobchart,
  getQCReport,
  clearQCReport,
  clearJobchartPreview,
  getJobchartPreview,
  getOldDepartmentTrackingOfJobchart,
  clearOldDepartmentTrackingOfJobchart,
} from '../Actions';
import Breadcrumbs from '../Components/Breadcrumbs';
import styles from '../styles';
import {
  dateFilterDropdown,
  dateSorter,
  onDateFilter,
  onFilter,
  sorter,
  filterDropdown,
  filterIcon,
} from '../utils';

const { TabPane } = Tabs;

const TrackingJobchart = props => {
  const { classes, match } = props;
  const dispatch = useDispatch();

  const departmentTrackingOfJobchart = useSelector(
    state => state.departmentTrackingOfJobchart
  );

  const oldDepartmentTrackingOfJobchart = useSelector(
    state => state.oldDepartmentTrackingOfJobchart
  );
  const issuedItemsOfJobchart = useSelector(
    state => state.issuedItemsOfJobchart
  );
  const mrr = useSelector(state => state.mrr);
  const outwardReport = useSelector(state => state.outwardReport);
  const rpoReport = useSelector(state => state.rpoReport);
  const qcReport = useSelector(state => state.qcReport);
  const jobchart = useSelector(state => state.jobchartPreview);

  useEffect(() => {
    dispatch(getRPOReport({ jobchartId: match.params.id }));
    dispatch(getOutwardReport({ jobchartId: match.params.id }));
    dispatch(getMRR({ jobchartId: match.params.id }));
    dispatch(getQCReport({ jobchartId: match.params.id }));
    dispatch(getIssuedItemsOfJobchart(match.params.id));
    dispatch(getJobchartPreview(match.params.id));
    dispatch(getDepartmentTrackingOfJobchart(match.params.id));
    dispatch(getOldDepartmentTrackingOfJobchart(match.params.id));

    return () => {
      dispatch(clearJobchartPreview());
      dispatch(clearDepartmentTrackingOfJobchart());
      dispatch(clearOldDepartmentTrackingOfJobchart());
      dispatch(clearRPOReport());
      dispatch(clearMRR());
      dispatch(clearOutwardReport());
      dispatch(clearIssuedItemsOfJobchart());
      dispatch(clearQCReport());
    };
  }, []);

  const rowKeyFabrics = useMemo(
    () => record =>
      `P${record.productId}Fb${record.fabricId}Wid${record.width}BC${
        record.baseColorId
      }PC${record.printColorId || ''}ChallanNo${record.challanNo || ''}`,
    []
  );

  const rowKeyAccessories = useMemo(
    () => record => `P${record.productId}A${record.accessoryId}`,
    []
  );

  const rowKeyStyles = useMemo(
    () => record =>
      `P${record.productId}S${record.styleId}BC${record.baseColor}PC${
        record.printColor || ''
      }EC${record.embroideryColorId || ''}AWC${
        record.acidWashColorId || ''
      }ChallanNo${record.challanNo || ''}`,
    []
  );

  const scrollSummaryTab = useMemo(() => ({ x: 1400 }), []);

  const fabricsStoreTab = useMemo(() => {
    const scroll = { x: 1200 };

    const columns = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Fabric',
        dataIndex: 'fabric',
      },
      {
        title: 'Width',
        dataIndex: 'width',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
        ],
      },
      {
        title: 'Jobchart Qty',
        dataIndex: 'jobchartQuantity',
      },
    ];

    if (departmentTrackingOfJobchart.fabricDepartmentFactories.inventory) {
      const children = [
        ...departmentTrackingOfJobchart.fabricDepartmentFactories.inventory.map(
          factory => {
            scroll.x += 80;
            return {
              title: factory,
              dataIndex: factory,
              render: (_text, record) => record.inventory[factory] || 0,
            };
          }
        ),
      ];

      if (
        departmentTrackingOfJobchart.fabricDepartmentFactories.inventory
          .length > 1
      ) {
        scroll.x += 80;

        children.push({
          title: 'Total',
          dataIndex: 'total',
          render: (_text, record) => {
            return Object.values(record.inventory).reduce(
              (acc, value) => +acc + +value,
              null
            );
          },
        });
      }

      columns.push({
        title: 'Inventory',
        children,
      });
    }

    if (departmentTrackingOfJobchart.fabricDepartmentFactories.checking) {
      const children = [
        ...departmentTrackingOfJobchart.fabricDepartmentFactories.checking.map(
          factory => {
            scroll.x += 80;

            return {
              title: factory,
              dataIndex: factory,
              render: (_text, record) => record.checking[factory] || null,
            };
          }
        ),
      ];

      if (
        departmentTrackingOfJobchart.fabricDepartmentFactories.checking.length >
        1
      ) {
        children.push({
          title: 'Total',
          dataIndex: 'total',
          render: (_text, record) => {
            return Object.values(record.checking).reduce(
              (acc, value) => +acc + +value,
              0
            );
          },
        });
      }

      columns.push({
        title: 'Checking',
        children,
      });
    }

    return { columns, scroll };
  }, [departmentTrackingOfJobchart]);

  const accessoriesStoreTab = useMemo(() => {
    const scroll = { x: 900 };

    const columns = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Accessory',
        dataIndex: 'accessory',
      },
      {
        title: 'Jobchart Qty',
        dataIndex: 'jobchartQuantity',
      },
    ];

    if (departmentTrackingOfJobchart.accessoryDepartmentFactories.inventory) {
      const children = [
        ...departmentTrackingOfJobchart.accessoryDepartmentFactories.inventory.map(
          factory => {
            scroll.x += 80;
            return {
              title: factory,
              dataIndex: factory,
              render: (_text, record) => record.inventory[factory] || 0,
            };
          }
        ),
      ];

      if (
        departmentTrackingOfJobchart.accessoryDepartmentFactories.inventory
          .length > 1
      ) {
        scroll.x += 80;

        children.push({
          title: 'Total',
          dataIndex: 'total',
          render: (_text, record) => {
            return Object.values(record.inventory).reduce(
              (acc, value) => +acc + +value,
              null
            );
          },
        });
      }

      columns.push({
        title: 'Inventory',
        children,
      });
    }

    if (departmentTrackingOfJobchart.accessoryDepartmentFactories.checking) {
      const children = [
        ...departmentTrackingOfJobchart.accessoryDepartmentFactories.checking.map(
          factory => {
            scroll.x += 80;

            return {
              title: factory,
              dataIndex: factory,
              render: (_text, record) => record.checking[factory] || null,
            };
          }
        ),
      ];

      if (
        departmentTrackingOfJobchart.accessoryDepartmentFactories.checking
          .length > 1
      ) {
        children.push({
          title: 'Total',
          dataIndex: 'total',
          render: (_text, record) => {
            return Object.values(record.checking).reduce(
              (acc, value) => +acc + +value,
              0
            );
          },
        });
      }

      columns.push({
        title: 'Checking',
        children,
      });
    }

    return { columns, scroll };
  }, [departmentTrackingOfJobchart]);

  const stylesStoreTab = useMemo(() => {
    const scroll = { x: 1400 };

    const columns = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Style',
        dataIndex: 'name',
      },
      {
        title: 'Description',
        dataIndex: 'description',
      },
      {
        title: 'Size',
        dataIndex: 'size',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
          {
            title: 'Embroidery',
            dataIndex: 'embroideryColor',
          },
          {
            title: 'Acid Wash',
            dataIndex: 'acidWashColor',
          },
        ],
      },
      {
        title: 'Jobchart Qty',
        dataIndex: 'jobchartQuantity',
      },
    ];

    if (departmentTrackingOfJobchart.styleDepartmentFactories.inventory) {
      const children = [
        ...departmentTrackingOfJobchart.styleDepartmentFactories.inventory.map(
          factory => {
            scroll.x += 80;
            return {
              title: factory,
              dataIndex: factory,
              render: (_text, record) => record.inventory[factory] || 0,
            };
          }
        ),
      ];

      if (
        departmentTrackingOfJobchart.styleDepartmentFactories.inventory.length >
        1
      ) {
        scroll.x += 80;

        children.push({
          title: 'Total',
          dataIndex: 'total',
          render: (_text, record) => {
            return Object.values(record.inventory).reduce(
              (acc, value) => +acc + +value,
              null
            );
          },
        });
      }

      columns.push({
        title: 'Inventory',
        children,
      });
    }

    if (departmentTrackingOfJobchart.styleDepartmentFactories.checking) {
      const children = [
        ...departmentTrackingOfJobchart.styleDepartmentFactories.checking.map(
          factory => {
            scroll.x += 80;

            return {
              title: factory,
              dataIndex: factory,
              render: (_text, record) => record.checking[factory] || null,
            };
          }
        ),
      ];

      if (
        departmentTrackingOfJobchart.styleDepartmentFactories.checking.length >
        1
      ) {
        children.push({
          title: 'Total',
          dataIndex: 'total',
          render: (_text, record) => {
            return Object.values(record.checking).reduce(
              (acc, value) => +acc + +value,
              0
            );
          },
        });
      }

      columns.push({
        title: 'Checking',
        children,
      });
    }

    return { columns, scroll };
  }, [departmentTrackingOfJobchart]);

  const fabricsCuttingTab = useMemo(() => {
    const scroll = { x: 1200 };

    let showTable = false;

    const columns = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Fabric',
        dataIndex: 'fabric',
      },
      {
        title: 'Width',
        dataIndex: 'width',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
        ],
      },
      {
        title: 'Jobchart Qty',
        dataIndex: 'jobchartQuantity',
      },
    ];

    if (departmentTrackingOfJobchart.fabricDepartmentFactories.precutting) {
      showTable = true;
      const children = [
        ...departmentTrackingOfJobchart.fabricDepartmentFactories.precutting.map(
          factory => {
            scroll.x += 80;
            return {
              title: factory,
              dataIndex: factory,
              render: (_text, record) => record.precutting[factory] || 0,
            };
          }
        ),
      ];

      if (
        departmentTrackingOfJobchart.fabricDepartmentFactories.precutting
          .length > 1
      ) {
        scroll.x += 80;

        children.push({
          title: 'Total',
          dataIndex: 'total',
          render: (_text, record) => {
            return Object.values(record.precutting).reduce(
              (acc, value) => +acc + +value,
              null
            );
          },
        });
      }

      columns.push({
        title: 'Pre Cutting',
        children,
      });
    }

    if (departmentTrackingOfJobchart.fabricDepartmentFactories.recutting) {
      showTable = true;
      const children = [
        ...departmentTrackingOfJobchart.fabricDepartmentFactories.recutting.map(
          factory => {
            scroll.x += 80;
            return {
              title: factory,
              dataIndex: factory,
              render: (_text, record) => record.recutting[factory] || 0,
            };
          }
        ),
      ];

      if (
        departmentTrackingOfJobchart.fabricDepartmentFactories.recutting
          .length > 1
      ) {
        scroll.x += 80;

        children.push({
          title: 'Total',
          dataIndex: 'total',
          render: (_text, record) => {
            return Object.values(record.recutting).reduce(
              (acc, value) => +acc + +value,
              null
            );
          },
        });
      }

      columns.push({
        title: 'Re Cutting',
        children,
      });
    }

    if (
      departmentTrackingOfJobchart.fabricDepartmentFactories['final cutting']
    ) {
      showTable = true;
      const children = [
        ...departmentTrackingOfJobchart.fabricDepartmentFactories[
          'final cutting'
        ].map(factory => {
          scroll.x += 80;
          return {
            title: factory,
            dataIndex: factory,
            render: (_text, record) => record['final cutting'][factory] || 0,
          };
        }),
      ];

      if (
        departmentTrackingOfJobchart.fabricDepartmentFactories['final cutting']
          .length > 1
      ) {
        scroll.x += 80;

        children.push({
          title: 'Total',
          dataIndex: 'total',
          render: (_text, record) => {
            return Object.values(record['final cutting']).reduce(
              (acc, value) => +acc + +value,
              null
            );
          },
        });
      }

      columns.push({
        title: 'Final Cutting',
        children,
      });
    }

    return { columns, scroll, showTable };
  }, [departmentTrackingOfJobchart]);

  const accessoriesCuttingTab = useMemo(() => {
    const scroll = { x: 1000 };

    let showTable = false;

    const columns = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Accessory',
        dataIndex: 'accessory',
      },
      {
        title: 'Jobchart Qty',
        dataIndex: 'jobchartQuantity',
      },
    ];

    if (departmentTrackingOfJobchart.accessoryDepartmentFactories.precutting) {
      showTable = true;
      const children = [
        ...departmentTrackingOfJobchart.accessoryDepartmentFactories.precutting.map(
          factory => {
            scroll.x += 80;
            return {
              title: factory,
              dataIndex: factory,
              render: (_text, record) => record.precutting[factory] || 0,
            };
          }
        ),
      ];

      if (
        departmentTrackingOfJobchart.accessoryDepartmentFactories.precutting
          .length > 1
      ) {
        scroll.x += 80;

        children.push({
          title: 'Total',
          dataIndex: 'total',
          render: (_text, record) => {
            return Object.values(record.precutting).reduce(
              (acc, value) => +acc + +value,
              null
            );
          },
        });
      }

      columns.push({
        title: 'Pre Cutting',
        children,
      });
    }

    if (departmentTrackingOfJobchart.accessoryDepartmentFactories.recutting) {
      showTable = true;
      const children = [
        ...departmentTrackingOfJobchart.accessoryDepartmentFactories.recutting.map(
          factory => {
            scroll.x += 80;
            return {
              title: factory,
              dataIndex: factory,
              render: (_text, record) => record.recutting[factory] || 0,
            };
          }
        ),
      ];

      if (
        departmentTrackingOfJobchart.accessoryDepartmentFactories.recutting
          .length > 1
      ) {
        scroll.x += 80;

        children.push({
          title: 'Total',
          dataIndex: 'total',
          render: (_text, record) => {
            return Object.values(record.recutting).reduce(
              (acc, value) => +acc + +value,
              null
            );
          },
        });
      }

      columns.push({
        title: 'Re Cutting',
        children,
      });
    }

    if (
      departmentTrackingOfJobchart.accessoryDepartmentFactories['final cutting']
    ) {
      showTable = true;
      const children = [
        ...departmentTrackingOfJobchart.accessoryDepartmentFactories[
          'final cutting'
        ].map(factory => {
          scroll.x += 80;
          return {
            title: factory,
            dataIndex: factory,
            render: (_text, record) => record['final cutting'][factory] || 0,
          };
        }),
      ];

      if (
        departmentTrackingOfJobchart.accessoryDepartmentFactories[
          'final cutting'
        ].length > 1
      ) {
        scroll.x += 80;

        children.push({
          title: 'Total',
          dataIndex: 'total',
          render: (_text, record) => {
            return Object.values(record['final cutting']).reduce(
              (acc, value) => +acc + +value,
              null
            );
          },
        });
      }

      columns.push({
        title: 'Final Cutting',
        children,
      });
    }

    return { columns, scroll, showTable };
  }, [departmentTrackingOfJobchart]);

  const stylesCuttingTab = useMemo(() => {
    const scroll = { x: 1400 };

    let showTable = false;

    const columns = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Style',
        dataIndex: 'name',
      },
      {
        title: 'Description',
        dataIndex: 'description',
      },
      {
        title: 'Size',
        dataIndex: 'size',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
          {
            title: 'Embroidery',
            dataIndex: 'embroideryColor',
          },
          {
            title: 'Acid Wash',
            dataIndex: 'acidWashColor',
          },
        ],
      },
      {
        title: 'Jobchart Qty',
        dataIndex: 'jobchartQuantity',
      },
    ];

    if (departmentTrackingOfJobchart.styleDepartmentFactories.precutting) {
      showTable = true;
      const children = [
        ...departmentTrackingOfJobchart.styleDepartmentFactories.precutting.map(
          factory => {
            scroll.x += 80;
            return {
              title: factory,
              dataIndex: factory,
              render: (_text, record) => record.precutting[factory] || 0,
            };
          }
        ),
      ];

      if (
        departmentTrackingOfJobchart.styleDepartmentFactories.precutting
          .length > 1
      ) {
        scroll.x += 80;

        children.push({
          title: 'Total',
          dataIndex: 'total',
          render: (_text, record) => {
            return Object.values(record.precutting).reduce(
              (acc, value) => +acc + +value,
              null
            );
          },
        });
      }

      columns.push({
        title: 'Pre Cutting',
        children,
      });
    }

    if (departmentTrackingOfJobchart.styleDepartmentFactories.recutting) {
      showTable = true;
      const children = [
        ...departmentTrackingOfJobchart.styleDepartmentFactories.recutting.map(
          factory => {
            scroll.x += 80;
            return {
              title: factory,
              dataIndex: factory,
              render: (_text, record) => record.recutting[factory] || 0,
            };
          }
        ),
      ];

      if (
        departmentTrackingOfJobchart.styleDepartmentFactories.recutting.length >
        1
      ) {
        scroll.x += 80;

        children.push({
          title: 'Total',
          dataIndex: 'total',
          render: (_text, record) => {
            return Object.values(record.recutting).reduce(
              (acc, value) => +acc + +value,
              null
            );
          },
        });
      }

      columns.push({
        title: 'Re Cutting',
        children,
      });
    }

    if (
      departmentTrackingOfJobchart.styleDepartmentFactories['final cutting']
    ) {
      showTable = true;
      const children = [
        ...departmentTrackingOfJobchart.styleDepartmentFactories[
          'final cutting'
        ].map(factory => {
          scroll.x += 80;
          return {
            title: factory,
            dataIndex: factory,
            render: (_text, record) => record['final cutting'][factory] || 0,
          };
        }),
      ];

      if (
        departmentTrackingOfJobchart.styleDepartmentFactories['final cutting']
          .length > 1
      ) {
        scroll.x += 80;

        children.push({
          title: 'Total',
          dataIndex: 'total',
          render: (_text, record) => {
            return Object.values(record['final cutting']).reduce(
              (acc, value) => +acc + +value,
              null
            );
          },
        });
      }

      columns.push({
        title: 'Final Cutting',
        children,
      });
    }

    return { columns, scroll, showTable };
  }, [departmentTrackingOfJobchart]);

  const fabricsWashingTab = useMemo(() => {
    let showTable = false;

    const scroll = { x: 1200 };

    const columns = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Fabric',
        dataIndex: 'fabric',
      },
      {
        title: 'Width',
        dataIndex: 'width',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
        ],
      },
      {
        title: 'Jobchart Qty',
        dataIndex: 'jobchartQuantity',
      },
    ];

    if (departmentTrackingOfJobchart.fabricDepartmentFactories['pre washing']) {
      showTable = true;
      const children = [
        ...departmentTrackingOfJobchart.fabricDepartmentFactories[
          'pre washing'
        ].map(factory => {
          scroll.x += 80;
          return {
            title: factory,
            dataIndex: factory,
            render: (_text, record) => record['pre washing'][factory] || 0,
          };
        }),
      ];

      if (
        departmentTrackingOfJobchart.fabricDepartmentFactories['pre washing']
          .length > 1
      ) {
        scroll.x += 80;

        children.push({
          title: 'Total',
          dataIndex: 'total',
          render: (_text, record) => {
            return Object.values(record['pre washing']).reduce(
              (acc, value) => +acc + +value,
              null
            );
          },
        });
      }

      columns.push({
        title: 'Pre Washing',
        children,
      });
    }

    if (
      departmentTrackingOfJobchart.fabricDepartmentFactories['final washing']
    ) {
      showTable = true;
      const children = [
        ...departmentTrackingOfJobchart.fabricDepartmentFactories[
          'final washing'
        ].map(factory => {
          scroll.x += 80;
          return {
            title: factory,
            dataIndex: factory,
            render: (_text, record) => record['final washing'][factory] || 0,
          };
        }),
      ];

      if (
        departmentTrackingOfJobchart.fabricDepartmentFactories['final washing']
          .length > 1
      ) {
        scroll.x += 80;

        children.push({
          title: 'Total',
          dataIndex: 'total',
          render: (_text, record) => {
            return Object.values(record['final washing']).reduce(
              (acc, value) => +acc + +value,
              null
            );
          },
        });
      }

      columns.push({
        title: 'Final Washing',
        children,
      });
    }

    return { columns, scroll, showTable };
  }, [departmentTrackingOfJobchart]);

  const accessoriesWashingTab = useMemo(() => {
    const scroll = { x: 1000 };
    let showTable = false;

    const columns = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Accessory',
        dataIndex: 'accessory',
      },
      {
        title: 'Jobchart Qty',
        dataIndex: 'jobchartQuantity',
      },
    ];

    if (
      departmentTrackingOfJobchart.accessoryDepartmentFactories['pre washing']
    ) {
      showTable = true;
      const children = [
        ...departmentTrackingOfJobchart.accessoryDepartmentFactories[
          'pre washing'
        ].map(factory => {
          scroll.x += 80;
          return {
            title: factory,
            dataIndex: factory,
            render: (_text, record) => record['pre washing'][factory] || 0,
          };
        }),
      ];

      if (
        departmentTrackingOfJobchart.accessoryDepartmentFactories['pre washing']
          .length > 1
      ) {
        scroll.x += 80;

        children.push({
          title: 'Total',
          dataIndex: 'total',
          render: (_text, record) => {
            return Object.values(record['pre washing']).reduce(
              (acc, value) => +acc + +value,
              null
            );
          },
        });
      }

      columns.push({
        title: 'Pre Washing',
        children,
      });
    }

    if (
      departmentTrackingOfJobchart.accessoryDepartmentFactories['final washing']
    ) {
      showTable = true;
      const children = [
        ...departmentTrackingOfJobchart.accessoryDepartmentFactories[
          'final washing'
        ].map(factory => {
          scroll.x += 80;
          return {
            title: factory,
            dataIndex: factory,
            render: (_text, record) => record['final washing'][factory] || 0,
          };
        }),
      ];

      if (
        departmentTrackingOfJobchart.accessoryDepartmentFactories[
          'final washing'
        ].length > 1
      ) {
        scroll.x += 80;

        children.push({
          title: 'Total',
          dataIndex: 'total',
          render: (_text, record) => {
            return Object.values(record['final washing']).reduce(
              (acc, value) => +acc + +value,
              null
            );
          },
        });
      }

      columns.push({
        title: 'Final Washing',
        children,
      });
    }

    return { columns, scroll, showTable };
  }, [departmentTrackingOfJobchart]);

  const stylesWashingTab = useMemo(() => {
    const scroll = { x: 1400 };
    let showTable = false;

    const columns = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Style',
        dataIndex: 'name',
      },
      {
        title: 'Description',
        dataIndex: 'description',
      },
      {
        title: 'Size',
        dataIndex: 'size',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
          {
            title: 'Embroidery',
            dataIndex: 'embroideryColor',
          },
          {
            title: 'Acid Wash',
            dataIndex: 'acidWashColor',
          },
        ],
      },
      {
        title: 'Jobchart Qty',
        dataIndex: 'jobchartQuantity',
      },
    ];

    if (departmentTrackingOfJobchart.styleDepartmentFactories['pre washing']) {
      console.log('here');
      showTable = true;
      const children = [
        ...departmentTrackingOfJobchart.styleDepartmentFactories[
          'pre washing'
        ].map(factory => {
          scroll.x += 80;
          return {
            title: factory,
            dataIndex: factory,
            render: (_text, record) => record['pre washing'][factory] || 0,
          };
        }),
      ];

      if (
        departmentTrackingOfJobchart.styleDepartmentFactories['pre washing']
          .length > 1
      ) {
        scroll.x += 80;

        children.push({
          title: 'Total',
          dataIndex: 'total',
          render: (_text, record) => {
            return Object.values(record['pre washing']).reduce(
              (acc, value) => +acc + +value,
              null
            );
          },
        });
      }

      columns.push({
        title: 'Pre Washing',
        children,
      });
    }

    if (
      departmentTrackingOfJobchart.styleDepartmentFactories['final washing']
    ) {
      showTable = true;
      const children = [
        ...departmentTrackingOfJobchart.styleDepartmentFactories[
          'final washing'
        ].map(factory => {
          scroll.x += 80;
          return {
            title: factory,
            dataIndex: factory,
            render: (_text, record) => record['final washing'][factory] || 0,
          };
        }),
      ];

      if (
        departmentTrackingOfJobchart.styleDepartmentFactories['final washing']
          .length > 1
      ) {
        scroll.x += 80;

        children.push({
          title: 'Total',
          dataIndex: 'total',
          render: (_text, record) => {
            return Object.values(record['final washing']).reduce(
              (acc, value) => +acc + +value,
              null
            );
          },
        });
      }

      columns.push({
        title: 'Final Washing',
        children,
      });
    }

    return { columns, scroll, showTable };
  }, [departmentTrackingOfJobchart]);

  const fabricsStitchingTab = useMemo(() => {
    let showTable = false;

    const scroll = { x: 1200 };

    const columns = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Fabric',
        dataIndex: 'fabric',
      },
      {
        title: 'Width',
        dataIndex: 'width',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
        ],
      },
      {
        title: 'Jobchart Qty',
        dataIndex: 'jobchartQuantity',
      },
    ];

    if (departmentTrackingOfJobchart.fabricDepartmentFactories.stitching) {
      showTable = true;
      const children = [
        ...departmentTrackingOfJobchart.fabricDepartmentFactories.stitching.map(
          factory => {
            scroll.x += 80;
            return {
              title: factory,
              dataIndex: factory,
              render: (_text, record) => record.stitching[factory] || 0,
            };
          }
        ),
      ];

      if (
        departmentTrackingOfJobchart.fabricDepartmentFactories.stitching
          .length > 1
      ) {
        scroll.x += 80;

        children.push({
          title: 'Total',
          dataIndex: 'total',
          render: (_text, record) => {
            return Object.values(record.stitching).reduce(
              (acc, value) => +acc + +value,
              null
            );
          },
        });
      }

      columns.push({
        title: 'Stitching',
        children,
      });
    }

    return { columns, scroll, showTable };
  }, [departmentTrackingOfJobchart]);

  const accessoriesStitchingTab = useMemo(() => {
    const scroll = { x: 1000 };
    let showTable = false;

    const columns = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Accessory',
        dataIndex: 'accessory',
      },
      {
        title: 'Jobchart Qty',
        dataIndex: 'jobchartQuantity',
      },
    ];

    if (departmentTrackingOfJobchart.accessoryDepartmentFactories.stitching) {
      showTable = true;
      const children = [
        ...departmentTrackingOfJobchart.accessoryDepartmentFactories.stitching.map(
          factory => {
            scroll.x += 80;
            return {
              title: factory,
              dataIndex: factory,
              render: (_text, record) => record.stitching[factory] || 0,
            };
          }
        ),
      ];

      if (
        departmentTrackingOfJobchart.accessoryDepartmentFactories.stitching
          .length > 1
      ) {
        scroll.x += 80;

        children.push({
          title: 'Total',
          dataIndex: 'total',
          render: (_text, record) => {
            return Object.values(record.stitching).reduce(
              (acc, value) => +acc + +value,
              null
            );
          },
        });
      }

      columns.push({
        title: 'Stitching',
        children,
      });
    }

    return { columns, scroll, showTable };
  }, [departmentTrackingOfJobchart]);

  const stylesStitchingTab = useMemo(() => {
    const scroll = { x: 1500 };
    let showTable = false;

    const columns = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Style',
        dataIndex: 'name',
      },
      {
        title: 'Description',
        dataIndex: 'description',
      },
      {
        title: 'Size',
        dataIndex: 'size',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
          {
            title: 'Embroidery',
            dataIndex: 'embroideryColor',
          },
          {
            title: 'Acid Wash',
            dataIndex: 'acidWashColor',
          },
        ],
      },
      {
        title: 'Jobchart Qty',
        dataIndex: 'jobchartQuantity',
      },
    ];

    if (departmentTrackingOfJobchart.styleDepartmentFactories.stitching) {
      showTable = true;
      const children = [
        ...departmentTrackingOfJobchart.styleDepartmentFactories.stitching.map(
          factory => {
            scroll.x += 80;
            return {
              title: factory,
              dataIndex: factory,
              render: (_text, record) => record.stitching[factory] || 0,
            };
          }
        ),
      ];

      if (
        departmentTrackingOfJobchart.styleDepartmentFactories.stitching.length >
        1
      ) {
        scroll.x += 80;

        children.push({
          title: 'Total',
          dataIndex: 'total',
          render: (_text, record) => {
            return Object.values(record.stitching).reduce(
              (acc, value) => +acc + +value,
              null
            );
          },
        });
      }

      columns.push({
        title: 'Stitching',
        children,
      });
    }

    return { columns, scroll, showTable };
  }, [departmentTrackingOfJobchart]);

  const fabricsFinishingTab = useMemo(() => {
    let showTable = false;

    const scroll = { x: 1200 };

    const columns = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Fabric',
        dataIndex: 'fabric',
      },
      {
        title: 'Width',
        dataIndex: 'width',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
        ],
      },
      {
        title: 'Jobchart Qty',
        dataIndex: 'jobchartQuantity',
      },
    ];

    if (departmentTrackingOfJobchart.fabricDepartmentFactories.finishing) {
      showTable = true;
      const children = [
        ...departmentTrackingOfJobchart.fabricDepartmentFactories.finishing.map(
          factory => {
            scroll.x += 80;
            return {
              title: factory,
              dataIndex: factory,
              render: (_text, record) => record.finishing[factory] || 0,
            };
          }
        ),
      ];

      if (
        departmentTrackingOfJobchart.fabricDepartmentFactories.finishing
          .length > 1
      ) {
        scroll.x += 80;

        children.push({
          title: 'Total',
          dataIndex: 'total',
          render: (_text, record) => {
            return Object.values(record.finishing).reduce(
              (acc, value) => +acc + +value,
              null
            );
          },
        });
      }

      columns.push({
        title: 'Finishing',
        children,
      });
    }

    return { columns, scroll, showTable };
  }, [departmentTrackingOfJobchart]);

  const accessoriesFinishingTab = useMemo(() => {
    const scroll = { x: 1000 };
    let showTable = false;

    const columns = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Accessory',
        dataIndex: 'accessory',
      },
      {
        title: 'Jobchart Qty',
        dataIndex: 'jobchartQuantity',
      },
    ];

    if (departmentTrackingOfJobchart.accessoryDepartmentFactories.finishing) {
      showTable = true;
      const children = [
        ...departmentTrackingOfJobchart.accessoryDepartmentFactories.finishing.map(
          factory => {
            scroll.x += 80;
            return {
              title: factory,
              dataIndex: factory,
              render: (_text, record) => record.finishing[factory] || 0,
            };
          }
        ),
      ];

      if (
        departmentTrackingOfJobchart.accessoryDepartmentFactories.finishing
          .length > 1
      ) {
        scroll.x += 80;

        children.push({
          title: 'Total',
          dataIndex: 'total',
          render: (_text, record) => {
            return Object.values(record.finishing).reduce(
              (acc, value) => +acc + +value,
              null
            );
          },
        });
      }

      columns.push({
        title: 'Finishing',
        children,
      });
    }

    return { columns, scroll, showTable };
  }, [departmentTrackingOfJobchart]);

  const stylesFinishingTab = useMemo(() => {
    const scroll = { x: 1500 };
    let showTable = false;

    const columns = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Style',
        dataIndex: 'name',
      },
      {
        title: 'Description',
        dataIndex: 'description',
      },
      {
        title: 'Size',
        dataIndex: 'size',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
          {
            title: 'Embroidery',
            dataIndex: 'embroideryColor',
          },
          {
            title: 'Acid Wash',
            dataIndex: 'acidWashColor',
          },
        ],
      },
      {
        title: 'Jobchart Qty',
        dataIndex: 'jobchartQuantity',
      },
    ];

    if (departmentTrackingOfJobchart.styleDepartmentFactories.finishing) {
      showTable = true;
      const children = [
        ...departmentTrackingOfJobchart.styleDepartmentFactories.finishing.map(
          factory => {
            scroll.x += 80;
            return {
              title: factory,
              dataIndex: factory,
              render: (_text, record) => record.finishing[factory] || 0,
            };
          }
        ),
      ];

      if (
        departmentTrackingOfJobchart.styleDepartmentFactories.finishing.length >
        1
      ) {
        scroll.x += 80;

        children.push({
          title: 'Total',
          dataIndex: 'total',
          render: (_text, record) => {
            return Object.values(record.finishing).reduce(
              (acc, value) => +acc + +value,
              null
            );
          },
        });
      }

      columns.push({
        title: 'Finishing',
        children,
      });
    }

    return { columns, scroll, showTable };
  }, [departmentTrackingOfJobchart]);

  const fabricsPackingTab = useMemo(() => {
    let showTable = false;

    const scroll = { x: 1200 };

    const columns = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Fabric',
        dataIndex: 'fabric',
      },
      {
        title: 'Width',
        dataIndex: 'width',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
        ],
      },
      {
        title: 'Jobchart Qty',
        dataIndex: 'jobchartQuantity',
      },
    ];

    if (departmentTrackingOfJobchart.fabricDepartmentFactories.packing) {
      showTable = true;
      const children = [
        ...departmentTrackingOfJobchart.fabricDepartmentFactories.packing.map(
          factory => {
            scroll.x += 80;
            return {
              title: factory,
              dataIndex: factory,
              render: (_text, record) => record.packing[factory] || 0,
            };
          }
        ),
      ];

      if (
        departmentTrackingOfJobchart.fabricDepartmentFactories.packing.length >
        1
      ) {
        scroll.x += 80;

        children.push({
          title: 'Total',
          dataIndex: 'total',
          render: (_text, record) => {
            return Object.values(record.packing).reduce(
              (acc, value) => +acc + +value,
              null
            );
          },
        });
      }

      columns.push({
        title: 'Packing',
        children,
      });
    }

    return { columns, scroll, showTable };
  }, [departmentTrackingOfJobchart]);

  const accessoriesPackingTab = useMemo(() => {
    const scroll = { x: 1000 };
    let showTable = false;

    const columns = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Accessory',
        dataIndex: 'accessory',
      },
      {
        title: 'Jobchart Qty',
        dataIndex: 'jobchartQuantity',
      },
    ];

    if (departmentTrackingOfJobchart.accessoryDepartmentFactories.packing) {
      showTable = true;
      const children = [
        ...departmentTrackingOfJobchart.accessoryDepartmentFactories.packing.map(
          factory => {
            scroll.x += 80;
            return {
              title: factory,
              dataIndex: factory,
              render: (_text, record) => record.packing[factory] || 0,
            };
          }
        ),
      ];

      if (
        departmentTrackingOfJobchart.accessoryDepartmentFactories.packing
          .length > 1
      ) {
        scroll.x += 80;

        children.push({
          title: 'Total',
          dataIndex: 'total',
          render: (_text, record) => {
            return Object.values(record.packing).reduce(
              (acc, value) => +acc + +value,
              null
            );
          },
        });
      }

      columns.push({
        title: 'Packing',
        children,
      });
    }

    return { columns, scroll, showTable };
  }, [departmentTrackingOfJobchart]);

  const stylesPackingTab = useMemo(() => {
    const scroll = { x: 1500 };
    let showTable = false;

    const columns = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Style',
        dataIndex: 'name',
      },
      {
        title: 'Description',
        dataIndex: 'description',
      },
      {
        title: 'Size',
        dataIndex: 'size',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
          {
            title: 'Embroidery',
            dataIndex: 'embroideryColor',
          },
          {
            title: 'Acid Wash',
            dataIndex: 'acidWashColor',
          },
        ],
      },
      {
        title: 'Jobchart Qty',
        dataIndex: 'jobchartQuantity',
      },
    ];

    if (departmentTrackingOfJobchart.styleDepartmentFactories.packing) {
      showTable = true;
      const children = [
        ...departmentTrackingOfJobchart.styleDepartmentFactories.packing.map(
          factory => {
            scroll.x += 80;
            return {
              title: factory,
              dataIndex: factory,
              render: (_text, record) => record.packing[factory] || 0,
            };
          }
        ),
      ];

      if (
        departmentTrackingOfJobchart.styleDepartmentFactories.packing.length > 1
      ) {
        scroll.x += 80;

        children.push({
          title: 'Total',
          dataIndex: 'total',
          render: (_text, record) => {
            return Object.values(record.packing).reduce(
              (acc, value) => +acc + +value,
              null
            );
          },
        });
      }

      columns.push({
        title: 'Packing',
        children,
      });
    }

    return { columns, scroll, showTable };
  }, [departmentTrackingOfJobchart]);

  const fabricsDispatchTab = useMemo(() => {
    let showTable = false;

    const scroll = { x: 1200 };

    const columns = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Fabric',
        dataIndex: 'fabric',
      },
      {
        title: 'Width',
        dataIndex: 'width',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
        ],
      },
      {
        title: 'Jobchart Qty',
        dataIndex: 'jobchartQuantity',
      },
    ];

    if (departmentTrackingOfJobchart.fabricDepartmentFactories.dispatch) {
      showTable = true;
      const children = [
        ...departmentTrackingOfJobchart.fabricDepartmentFactories.dispatch.map(
          factory => {
            scroll.x += 80;
            return {
              title: factory,
              dataIndex: factory,
              render: (_text, record) => record.dispatch[factory] || 0,
            };
          }
        ),
      ];

      if (
        departmentTrackingOfJobchart.fabricDepartmentFactories.dispatch.length >
        1
      ) {
        scroll.x += 80;

        children.push({
          title: 'Total',
          dataIndex: 'total',
          render: (_text, record) => {
            return Object.values(record.dispatch).reduce(
              (acc, value) => +acc + +value,
              null
            );
          },
        });
      }

      columns.push({
        title: 'Dispatch',
        children,
      });
    }

    return { columns, scroll, showTable };
  }, [departmentTrackingOfJobchart]);

  const accessoriesDispatchTab = useMemo(() => {
    const scroll = { x: 1000 };
    let showTable = false;

    const columns = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Accessory',
        dataIndex: 'accessory',
      },
      {
        title: 'Jobchart Qty',
        dataIndex: 'jobchartQuantity',
      },
    ];

    if (departmentTrackingOfJobchart.accessoryDepartmentFactories.dispatch) {
      showTable = true;
      const children = [
        ...departmentTrackingOfJobchart.accessoryDepartmentFactories.dispatch.map(
          factory => {
            scroll.x += 80;
            return {
              title: factory,
              dataIndex: factory,
              render: (_text, record) => record.dispatch[factory] || 0,
            };
          }
        ),
      ];

      if (
        departmentTrackingOfJobchart.accessoryDepartmentFactories.dispatch
          .length > 1
      ) {
        scroll.x += 80;

        children.push({
          title: 'Total',
          dataIndex: 'total',
          render: (_text, record) => {
            return Object.values(record.dispatch).reduce(
              (acc, value) => +acc + +value,
              null
            );
          },
        });
      }

      columns.push({
        title: 'Dispatch',
        children,
      });
    }

    return { columns, scroll, showTable };
  }, [departmentTrackingOfJobchart]);

  const stylesDispatchTab = useMemo(() => {
    const scroll = { x: 1500 };
    let showTable = false;

    const columns = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Style',
        dataIndex: 'name',
      },
      {
        title: 'Description',
        dataIndex: 'description',
      },
      {
        title: 'Size',
        dataIndex: 'size',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
          {
            title: 'Embroidery',
            dataIndex: 'embroideryColor',
          },
          {
            title: 'Acid Wash',
            dataIndex: 'acidWashColor',
          },
        ],
      },
      {
        title: 'Jobchart Qty',
        dataIndex: 'jobchartQuantity',
      },
    ];

    if (departmentTrackingOfJobchart.styleDepartmentFactories.dispatch) {
      showTable = true;
      const children = [
        ...departmentTrackingOfJobchart.styleDepartmentFactories.dispatch.map(
          factory => {
            scroll.x += 80;
            return {
              title: factory,
              dataIndex: factory,
              render: (_text, record) => record.dispatch[factory] || 0,
            };
          }
        ),
      ];

      if (
        departmentTrackingOfJobchart.styleDepartmentFactories.dispatch.length >
        1
      ) {
        scroll.x += 80;

        children.push({
          title: 'Total',
          dataIndex: 'total',
          render: (_text, record) => {
            return Object.values(record.dispatch).reduce(
              (acc, value) => +acc + +value,
              null
            );
          },
        });
      }

      columns.push({
        title: 'Dispatch',
        children,
      });
    }

    return { columns, scroll, showTable };
  }, [departmentTrackingOfJobchart]);

  const summaryColumns = useMemo(() => {
    const _columns = [
      {
        title: 'Process',
        dataIndex: 'process',
        sorter: sorter('process'),
        onFilter: onFilter('process'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Item Type',
        dataIndex: 'type',
        sorter: sorter('type'),
        onFilter: onFilter('type'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Item',
        dataIndex: 'item',
        sorter: sorter('item'),
        onFilter: onFilter('item'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Quantity',
        dataIndex: 'quantity',
        sorter: sorter('quantity'),
        onFilter: onFilter('quantity'),
        filterDropdown,
        filterIcon,
      },
    ];

    return _columns;
  }, []);

  const rpoItemsColumns = useMemo(() => {
    const _columns = [
      {
        title: 'RPO #',
        dataIndex: 'rpoNo',
        sorter: sorter('rpoNo'),
        onFilter: onFilter('rpoNo'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Date',
        dataIndex: 'rpoDate',
        sorter: dateSorter('rpoDate'),
        onFilter: onDateFilter('rpoDate'),
        filterDropdown: dateFilterDropdown,
        filterIcon,
        render: text => (text ? moment(text).format('DD MMM YYYY') : null),
      },
      {
        title: 'Vendor',
        dataIndex: 'vendor',
        sorter: sorter('vendor'),
        onFilter: onFilter('vendor'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Process',
        dataIndex: 'process',
        sorter: sorter('process'),
        onFilter: onFilter('process'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Item Type',
        dataIndex: 'type',
        sorter: sorter('type'),
        onFilter: onFilter('type'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Item',
        dataIndex: 'item',
        sorter: sorter('item'),
        onFilter: onFilter('item'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Quantity',
        dataIndex: 'quantity',
        sorter: sorter('quantity'),
        onFilter: onFilter('quantity'),
        filterDropdown,
        filterIcon,
      },
    ];

    return _columns;
  }, []);

  const outwardItemsColumns = useMemo(() => {
    const _columns = [
      {
        title: 'Outward #',
        dataIndex: 'outwardNo',
        sorter: sorter('outwardNo'),
        onFilter: onFilter('outwardNo'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Date',
        dataIndex: 'outwardDate',
        sorter: dateSorter('outwardDate'),
        onFilter: onDateFilter('outwardDate'),
        filterDropdown: dateFilterDropdown,
        filterIcon,
        render: text => (text ? moment(text).format('DD MMM YYYY') : null),
      },
      {
        title: 'Vendor',
        dataIndex: 'vendor',
        sorter: sorter('vendor'),
        onFilter: onFilter('vendor'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'RPO #',
        dataIndex: 'rpoNo',
        sorter: sorter('rpoNo'),
        onFilter: onFilter('rpoNo'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Process',
        dataIndex: 'process',
        sorter: sorter('process'),
        onFilter: onFilter('process'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Item Type',
        dataIndex: 'type',
        sorter: sorter('type'),
        onFilter: onFilter('type'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Item',
        dataIndex: 'item',
        sorter: sorter('item'),
        onFilter: onFilter('item'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Quantity',
        dataIndex: 'quantity',
        sorter: sorter('quantity'),
        onFilter: onFilter('quantity'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Packages',
        dataIndex: 'packages',
        sorter: sorter('packages'),
        onFilter: onFilter('packages'),
        filterDropdown,
        filterIcon,
      },
    ];

    return _columns;
  }, []);

  const inwardItemsColumns = useMemo(() => {
    const _columns = [
      {
        title: 'Inward #',
        dataIndex: 'inwardNo',
        sorter: sorter('inwardNo'),
        onFilter: onFilter('inwardNo'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Date',
        dataIndex: 'inwardDate',
        sorter: dateSorter('inwardDate'),
        onFilter: onDateFilter('inwardDate'),
        filterDropdown: dateFilterDropdown,
        filterIcon,
        render: text => (text ? moment(text).format('DD MMM YYYY') : null),
      },
      {
        title: 'Vendor',
        dataIndex: 'vendor',
        sorter: sorter('vendor'),
        onFilter: onFilter('vendor'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'RPO #',
        dataIndex: 'rpoNo',
        sorter: sorter('rpoNo'),
        onFilter: onFilter('rpoNo'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Process',
        dataIndex: 'process',
        sorter: sorter('process'),
        onFilter: onFilter('process'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Item Type',
        dataIndex: 'type',
        sorter: sorter('type'),
        onFilter: onFilter('type'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Item',
        dataIndex: 'item',
        sorter: sorter('item'),
        onFilter: onFilter('item'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Actual Rate',
        dataIndex: 'actualRate',
        sorter: sorter('actualRate'),
        onFilter: onFilter('actualRate'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Quantity',
        children: [
          {
            title: 'Quantity',
            dataIndex: 'quantity',
            sorter: sorter('quantity'),
            onFilter: onFilter('quantity'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Accepted',
            dataIndex: 'accepted',
            sorter: sorter('accepted'),
            onFilter: onFilter('accepted'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Altered',
            dataIndex: 'altered',
            sorter: sorter('altered'),
            onFilter: onFilter('altered'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Rejected',
            dataIndex: 'rejected',
            sorter: sorter('rejected'),
            onFilter: onFilter('rejected'),
            filterDropdown,
            filterIcon,
          },
        ],
      },
      {
        title: 'Invoice',
        children: [
          {
            title: '#',
            dataIndex: 'invoice',
            sorter: sorter('invoice'),
            onFilter: onFilter('invoice'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Date',
            dataIndex: 'invoiceDate',
            sorter: dateSorter('invoiceDate'),
            onFilter: onDateFilter('invoiceDate'),
            filterDropdown: dateFilterDropdown,
            filterIcon,
            render: text => (text ? moment(text).format('DD MMM YYYY') : null),
          },
        ],
      },
      {
        title: 'Invoice',
        children: [
          {
            title: '#',
            dataIndex: 'challan',
            sorter: sorter('challan'),
            onFilter: onFilter('challan'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Date',
            dataIndex: 'challanDate',
            sorter: dateSorter('challanDate'),
            onFilter: onDateFilter('challanDate'),
            filterDropdown: dateFilterDropdown,
            filterIcon,
            render: text => (text ? moment(text).format('DD MMM YYYY') : null),
          },
        ],
      },
      {
        title: 'Processed',
        dataIndex: 'isProcessed',
        render: text => (text ? 'Yes' : 'No'),
      },
      {
        title: 'Packages',
        dataIndex: 'packages',
        sorter: sorter('packages'),
        onFilter: onFilter('packages'),
        filterDropdown,
        filterIcon,
      },
    ];

    return _columns;
  }, []);

  const inwardSummaryColumns = useMemo(() => {
    const _columns = [
      {
        title: 'Process',
        dataIndex: 'process',
        sorter: sorter('process'),
        onFilter: onFilter('process'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Item Type',
        dataIndex: 'type',
        sorter: sorter('type'),
        onFilter: onFilter('type'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Item',
        dataIndex: 'item',
        sorter: sorter('item'),
        onFilter: onFilter('item'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Quantity',
        children: [
          {
            title: 'Quantity',
            dataIndex: 'quantity',
            sorter: sorter('quantity'),
            onFilter: onFilter('quantity'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Accepted',
            dataIndex: 'accepted',
            sorter: sorter('accepted'),
            onFilter: onFilter('accepted'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Altered',
            dataIndex: 'altered',
            sorter: sorter('altered'),
            onFilter: onFilter('altered'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Rejected',
            dataIndex: 'rejected',
            sorter: sorter('rejected'),
            onFilter: onFilter('rejected'),
            filterDropdown,
            filterIcon,
          },
        ],
      },
    ];

    return _columns;
  }, []);

  const qcItemsColumns = useMemo(() => {
    const _columns = [
      {
        title: 'QC #',
        dataIndex: 'qcNo',
        sorter: sorter('qcNo'),
        onFilter: onFilter('qcNo'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'QC Date',
        dataIndex: 'qcDate',
        sorter: dateSorter('qcDate'),
        onFilter: onDateFilter('qcDate'),
        filterDropdown: dateFilterDropdown,
        filterIcon,
        render: text => (text ? moment(text).format('DD MMM YYYY') : null),
      },
      {
        title: 'Vendor',
        dataIndex: 'vendor',
        sorter: sorter('vendor'),
        onFilter: onFilter('vendor'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'RPO #',
        dataIndex: 'rpoNo',
        sorter: sorter('rpoNo'),
        onFilter: onFilter('rpoNo'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Process',
        dataIndex: 'process',
        sorter: sorter('process'),
        onFilter: onFilter('process'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Item Type',
        dataIndex: 'type',
        sorter: sorter('type'),
        onFilter: onFilter('type'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Product',
        dataIndex: 'product',
        sorter: sorter('product'),
        onFilter: onFilter('product'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Item',
        dataIndex: 'item',
        sorter: sorter('item'),
        onFilter: onFilter('item'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Quantity',
        children: [
          {
            title: 'Accepted',
            dataIndex: 'accepted',
            sorter: sorter('accepted'),
            onFilter: onFilter('accepted'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Altered',
            dataIndex: 'altered',
            sorter: sorter('altered'),
            onFilter: onFilter('altered'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Rejected',
            dataIndex: 'rejected',
            sorter: sorter('rejected'),
            onFilter: onFilter('rejected'),
            filterDropdown,
            filterIcon,
          },
        ],
      },
    ];

    return _columns;
  }, []);

  const qcSummaryColumns = useMemo(() => {
    const _columns = [
      {
        title: 'Vendor',
        dataIndex: 'vendor',
        sorter: sorter('vendor'),
        onFilter: onFilter('vendor'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Process',
        dataIndex: 'process',
        sorter: sorter('process'),
        onFilter: onFilter('process'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Item Type',
        dataIndex: 'type',
        sorter: sorter('type'),
        onFilter: onFilter('type'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Product',
        dataIndex: 'product',
        sorter: sorter('product'),
        onFilter: onFilter('product'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Item',
        dataIndex: 'item',
        sorter: sorter('item'),
        onFilter: onFilter('item'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Quantity',
        children: [
          {
            title: 'Accepted',
            dataIndex: 'accepted',
            sorter: sorter('accepted'),
            onFilter: onFilter('accepted'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Altered',
            dataIndex: 'altered',
            sorter: sorter('altered'),
            onFilter: onFilter('altered'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Rejected',
            dataIndex: 'rejected',
            sorter: sorter('rejected'),
            onFilter: onFilter('rejected'),
            filterDropdown,
            filterIcon,
          },
        ],
      },
    ];

    return _columns;
  }, []);

  const issueStyleColumns = useMemo(
    () => [
      {
        title: 'Challan #',
        dataIndex: 'challanNo',
        sorter: sorter('challanNo'),
        onFilter: onFilter('challanNo'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Date',
        dataIndex: 'createdAt',
        sorter: dateSorter('createdAt'),
        onFilter: onDateFilter('createdAt'),
        filterDropdown: dateFilterDropdown,
        filterIcon,
        render: text => (text ? moment(text).format('DD MMM YYYY') : null),
      },
      {
        title: 'From',
        children: [
          {
            title: 'From',
            dataIndex: 'from',
            sorter: sorter('from'),
            onFilter: onFilter('from'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'User',
            dataIndex: 'issuedBy',
            sorter: sorter('issuedBy'),
            onFilter: onFilter('issuedBy'),
            filterDropdown,
            filterIcon,
          },
        ],
      },
      {
        title: 'To',
        children: [
          {
            title: 'To',
            dataIndex: 'to',
            sorter: sorter('to'),
            onFilter: onFilter('to'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'User',
            dataIndex: 'receivedBy',
            sorter: sorter('receivedBy'),
            onFilter: onFilter('receivedBy'),
            filterDropdown,
            filterIcon,
          },
        ],
      },
      {
        title: 'Product',
        dataIndex: 'product',
        sorter: sorter('product'),
        onFilter: onFilter('product'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Style',
        dataIndex: 'style',
        sorter: sorter('style'),
        onFilter: onFilter('style'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Description',
        dataIndex: 'description',
        sorter: sorter('description'),
        onFilter: onFilter('description'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Size',
        dataIndex: 'size',
        sorter: sorter('size'),
        onFilter: onFilter('size'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
            sorter: sorter('baseColor'),
            onFilter: onFilter('baseColor'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
            sorter: sorter('printColor'),
            onFilter: onFilter('printColor'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Embroidery',
            dataIndex: 'embroideryColor',
            sorter: sorter('embroideryColor'),
            onFilter: onFilter('embroideryColor'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Acid Wash',
            dataIndex: 'acidWashColor',
            sorter: sorter('acidWashColor'),
            onFilter: onFilter('acidWashColor'),
            filterDropdown,
            filterIcon,
          },
        ],
      },
      {
        title: 'Quantity',
        dataIndex: 'quantity',
        sorter: sorter('quantity'),
        onFilter: onFilter('quantity'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Remark',
        dataIndex: 'remark',
        sorter: sorter('remark'),
        onFilter: onFilter('remark'),
        filterDropdown,
        filterIcon,
      },
    ],
    []
  );

  const issueFabricColumns = useMemo(
    () => [
      {
        title: 'Challan #',
        dataIndex: 'challanNo',
        sorter: sorter('challanNo'),
        onFilter: onFilter('challanNo'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Date',
        dataIndex: 'createdAt',
        sorter: dateSorter('createdAt'),
        onFilter: onDateFilter('createdAt'),
        filterDropdown: dateFilterDropdown,
        filterIcon,
        render: text => (text ? moment(text).format('DD MMM YYYY') : null),
      },
      {
        title: 'From',
        children: [
          {
            title: 'From',
            dataIndex: 'from',
            sorter: sorter('from'),
            onFilter: onFilter('from'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'User',
            dataIndex: 'issuedBy',
            sorter: sorter('issuedBy'),
            onFilter: onFilter('issuedBy'),
            filterDropdown,
            filterIcon,
          },
        ],
      },
      {
        title: 'To',
        children: [
          {
            title: 'To',
            dataIndex: 'to',
            sorter: sorter('to'),
            onFilter: onFilter('to'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'User',
            dataIndex: 'receivedBy',
            sorter: sorter('receivedBy'),
            onFilter: onFilter('receivedBy'),
            filterDropdown,
            filterIcon,
          },
        ],
      },
      {
        title: 'Product',
        dataIndex: 'product',
        sorter: sorter('product'),
        onFilter: onFilter('product'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Fabric',
        dataIndex: 'fabric',
        sorter: sorter('fabric'),
        onFilter: onFilter('fabric'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Width',
        dataIndex: 'width',
        sorter: sorter('width'),
        onFilter: onFilter('width'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
            sorter: sorter('baseColor'),
            onFilter: onFilter('baseColor'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
            sorter: sorter('printColor'),
            onFilter: onFilter('printColor'),
            filterDropdown,
            filterIcon,
          },
        ],
      },
      {
        title: 'Quantity',
        dataIndex: 'quantity',
        sorter: sorter('quantity'),
        onFilter: onFilter('quantity'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Remark',
        dataIndex: 'remark',
        sorter: sorter('remark'),
        onFilter: onFilter('remark'),
        filterDropdown,
        filterIcon,
      },
    ],
    []
  );

  const issueAccessoryColumns = useMemo(
    () => [
      {
        title: 'Challan #',
        dataIndex: 'challanNo',
        sorter: sorter('challanNo'),
        onFilter: onFilter('challanNo'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Date',
        dataIndex: 'createdAt',
        sorter: dateSorter('createdAt'),
        onFilter: onDateFilter('createdAt'),
        filterDropdown: dateFilterDropdown,
        filterIcon,
        render: text => (text ? moment(text).format('DD MMM YYYY') : null),
      },
      {
        title: 'From',
        children: [
          {
            title: 'From',
            dataIndex: 'from',
            sorter: sorter('from'),
            onFilter: onFilter('from'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'User',
            dataIndex: 'issuedBy',
            sorter: sorter('issuedBy'),
            onFilter: onFilter('issuedBy'),
            filterDropdown,
            filterIcon,
          },
        ],
      },
      {
        title: 'To',
        children: [
          {
            title: 'To',
            dataIndex: 'to',
            sorter: sorter('to'),
            onFilter: onFilter('to'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'User',
            dataIndex: 'receivedBy',
            sorter: sorter('receivedBy'),
            onFilter: onFilter('receivedBy'),
            filterDropdown,
            filterIcon,
          },
        ],
      },
      {
        title: 'Product',
        dataIndex: 'product',
        sorter: sorter('product'),
        onFilter: onFilter('product'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Accessory',
        dataIndex: 'accessory',
        sorter: sorter('accessory'),
        onFilter: onFilter('accessory'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Quantity',
        dataIndex: 'quantity',
        sorter: sorter('quantity'),
        onFilter: onFilter('quantity'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Remark',
        dataIndex: 'remark',
        sorter: sorter('remark'),
        onFilter: onFilter('remark'),
        filterDropdown,
        filterIcon,
      },
    ],
    []
  );

  const rowKeyItems = useMemo(
    () => record =>
      `B${record.bpoNo}R${record.rpoNo}Pr${record.process}T${record.type}It${record.item}Qty${record.quantity}`,
    []
  );

  const rowKeyItemsSummary = useMemo(
    () => record =>
      `Pr${record.process}T${record.type}It${record.item}Qty${record.quantity}`,
    []
  );

  const rowKeyQCItems = useMemo(
    () => record =>
      `Q${record.qcNo}V${record.vendor}R${record.rpoNo}T${record.type}P${record.product}It${record.item}A${record.accpeted}R${record.rejected}A${record.altered}`,
    []
  );

  const rowKeyQCItemsSummary = useMemo(
    () => record =>
      `V${record.vendor}P${record.process}T${record.type}P${record.product}It${record.item}A${record.accpeted}R${record.rejected}A${record.altered}`,
    []
  );

  const columnsFabricsFabricTab = useMemo(() => {
    const _columns = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Fabric',
        dataIndex: 'fabric',
      },
      {
        title: 'Width',
        dataIndex: 'width',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
        ],
      },
      {
        title: 'Jobchart Qty',
        dataIndex: 'jobchartQuantity',
      },
      {
        title: 'RPO Qty',
        dataIndex: 'rpoQuantity',
      },
      {
        title: 'Outward',
        children: [
          {
            title: 'Qty',
            dataIndex: 'totalSent',
          },
          {
            title: 'Bal',
            dataIndex: 'outwardBalance',
          },
        ],
      },
      {
        title: 'Inward',
        children: [
          {
            title: 'Qty',
            dataIndex: 'totalReceived',
          },
          {
            title: 'Bal',
            dataIndex: 'inwardBalance',
          },
        ],
      },
      {
        title: 'Quality Check',
        children: [
          {
            title: 'Acpt',
            dataIndex: 'acceptedQuantity',
          },
          {
            title: 'Alter',
            dataIndex: 'alteredQuantity',
          },
          {
            title: 'Rej',
            dataIndex: 'rejectedQuantity',
          },
        ],
      },
    ];

    return _columns;
  }, []);

  const columnsAccessoriesFabricTab = useMemo(() => {
    const column = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Accessory',
        dataIndex: 'accessory',
      },
      {
        title: 'Jobchart Qty',
        dataIndex: 'jobchartQuantity',
      },
      {
        title: 'RPO Qty',
        dataIndex: 'rpoQuantity',
      },
      {
        title: 'Outward',
        children: [
          {
            title: 'Qty',
            dataIndex: 'totalSent',
          },
          {
            title: 'Bal',
            dataIndex: 'outwardBalance',
          },
        ],
      },
      {
        title: 'Inward',
        children: [
          {
            title: 'Qty',
            dataIndex: 'totalReceived',
          },
          {
            title: 'Bal',
            dataIndex: 'inwardBalance',
          },
        ],
      },
      {
        title: 'Quality Check',
        children: [
          {
            title: 'Acpt',
            dataIndex: 'acceptedQuantity',
          },
          {
            title: 'Alter',
            dataIndex: 'alteredQuantity',
          },
          {
            title: 'Rej',
            dataIndex: 'rejectedQuantity',
          },
        ],
      },
    ];

    return column;
  }, []);

  const columnsStylesFabricTab = useMemo(() => {
    const column = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Style',
        dataIndex: 'name',
      },
      {
        title: 'Description',
        dataIndex: 'description',
      },
      {
        title: 'Size',
        dataIndex: 'size',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
          {
            title: 'Embroidery',
            dataIndex: 'embroideryColor',
          },
          {
            title: 'Acid Wash',
            dataIndex: 'acidWashColor',
          },
        ],
      },
      {
        title: 'Jobchart Qty',
        dataIndex: 'jobchartQuantity',
      },
      {
        title: 'RPO Qty',
        dataIndex: 'rpoQuantity',
      },
      {
        title: 'Outward',
        children: [
          {
            title: 'Qty',
            dataIndex: 'totalSent',
          },
          {
            title: 'Bal',
            dataIndex: 'outwardBalance',
          },
        ],
      },
      {
        title: 'Inward',
        children: [
          {
            title: 'Qty',
            dataIndex: 'totalReceived',
          },
          {
            title: 'Bal',
            dataIndex: 'inwardBalance',
          },
        ],
      },
      {
        title: 'Quality Check',
        children: [
          {
            title: 'Acpt',
            dataIndex: 'acceptedQuantity',
          },
          {
            title: 'Alter',
            dataIndex: 'alteredQuantity',
          },
          {
            title: 'Rej',
            dataIndex: 'rejectedQuantity',
          },
        ],
      },
    ];

    return column;
  }, []);

  const fabricsTracking = useMemo(() => {
    const scroll = { x: 1600 };

    const columns = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Fabric',
        dataIndex: 'fabric',
      },
      {
        title: 'Width',
        dataIndex: 'width',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
        ],
      },
      {
        title: 'Jobchart Qty',
        dataIndex: 'jobchartQuantity',
      },
      {
        title: 'Cutting',
        dataIndex: 'cutting',
        render: el =>
          +el.total || +el.current ? `${+el.total} / ${el.current}` : null,
      },
      {
        title: 'washing',
        dataIndex: 'washing',
        render: el =>
          +el.total || +el.current ? `${+el.total} / ${el.current}` : null,
      },
      {
        title: 'stitching',
        dataIndex: 'stitching',
        render: el =>
          +el.total || +el.current ? `${+el.total} / ${el.current}` : null,
      },
      {
        title: 'finishing',
        dataIndex: 'finishing',
        render: el =>
          +el.total || +el.current ? `${+el.total} / ${el.current}` : null,
      },
      {
        title: 'packing',
        dataIndex: 'packing',
        render: el =>
          +el.total || +el.current ? `${+el.total} / ${el.current}` : null,
      },
      {
        title: 'dispatch',
        dataIndex: 'dispatch',
        render: el =>
          +el.total || +el.current ? `${+el.total} / ${el.current}` : null,
      },
    ];

    return { columns, scroll };
  }, [departmentTrackingOfJobchart]);

  const styleTracking = useMemo(() => {
    const scroll = { x: 1900 };

    const columns = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Style',
        dataIndex: 'style',
      },
      {
        title: 'Description',
        dataIndex: 'description',
      },
      {
        title: 'Size',
        dataIndex: 'size',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
          {
            title: 'Embroidery',
            dataIndex: 'embroideryColor',
          },
          {
            title: 'Acid Wash',
            dataIndex: 'acidWashColor',
          },
        ],
      },
      {
        title: 'Jobchart Qty',
        dataIndex: 'jobchartQuantity',
      },
      {
        title: 'Cutting',
        dataIndex: 'cutting',
        render: el =>
          +el.total || +el.current ? `${+el.total} / ${el.current}` : null,
      },
      {
        title: 'washing',
        dataIndex: 'washing',
        render: el =>
          +el.total || +el.current ? `${+el.total} / ${el.current}` : null,
      },
      {
        title: 'stitching',
        dataIndex: 'stitching',
        render: el =>
          +el.total || +el.current ? `${+el.total} / ${el.current}` : null,
      },
      {
        title: 'finishing',
        dataIndex: 'finishing',
        render: el =>
          +el.total || +el.current ? `${+el.total} / ${el.current}` : null,
      },
      {
        title: 'packing',
        dataIndex: 'packing',
        render: el =>
          +el.total || +el.current ? `${+el.total} / ${el.current}` : null,
      },
      {
        title: 'dispatch',
        dataIndex: 'dispatch',
        render: el =>
          +el.total || +el.current ? `${+el.total} / ${el.current}` : null,
      },
    ];

    return { columns, scroll };
  }, [departmentTrackingOfJobchart]);

  return (
    <>
      <Breadcrumbs
        moduleName="Jobchart"
        moduleLink="/jobcharts/list"
        subModuleName="Tracking"
      />
      <div className={classes.tableStyle}>
        <Descriptions column={2} size="small" bordered>
          <Descriptions.Item label="Customer">
            {jobchart.customer}
          </Descriptions.Item>
          <Descriptions.Item label="BPO #">{jobchart.bpoNo}</Descriptions.Item>
          <Descriptions.Item label="BPO Date">
            {jobchart.jobDate
              ? moment(jobchart.jobDate).format('DD MMM YYYY')
              : null}
          </Descriptions.Item>
          <Descriptions.Item label="Ship Date">
            {jobchart.shipDate
              ? moment(jobchart.shipDate).format('DD MMM YYYY')
              : null}
          </Descriptions.Item>
          <Descriptions.Item label="Remark">
            {jobchart.remark}
          </Descriptions.Item>
        </Descriptions>
        <br />
        <Tabs defaultActiveKey="summary" type="card">
          <TabPane tab="Summary" key="summary">
            <Tabs defaultActiveKey="RPO Data" type="card">
              {rpoReport.items?.length || rpoReport.summary?.length ? (
                <TabPane tab="RPO Data" key="RPO Data">
                  <div className={classes.previewMainHeading}>RPO Tables</div>
                  <br />
                  <Table
                    bordered
                    size="small"
                    columns={rpoItemsColumns}
                    dataSource={rpoReport.items}
                    rowKey={rowKeyItems}
                    pagination={false}
                    title={() => 'Items'}
                    scroll={scrollSummaryTab}
                  />
                  <br />
                  <Table
                    bordered
                    size="small"
                    columns={summaryColumns}
                    dataSource={rpoReport.summary}
                    rowKey={rowKeyItemsSummary}
                    pagination={false}
                    title={() => 'Items Summary'}
                    scroll={scrollSummaryTab}
                  />
                  <br />
                  <br />
                </TabPane>
              ) : null}
              {outwardReport.items?.length || outwardReport.summary?.length ? (
                <TabPane tab="Outward Data" key="Outward Data">
                  <div className={classes.previewMainHeading}>
                    Outward Tables
                  </div>
                  <br />
                  <Table
                    bordered
                    size="small"
                    columns={outwardItemsColumns}
                    dataSource={outwardReport.items}
                    rowKey={rowKeyItems}
                    pagination={false}
                    title={() => 'Items'}
                    scroll={scrollSummaryTab}
                  />
                  <br />
                  <Table
                    bordered
                    size="small"
                    columns={summaryColumns}
                    dataSource={outwardReport.summary}
                    rowKey={rowKeyItemsSummary}
                    pagination={false}
                    title={() => 'Items Summary'}
                    scroll={scrollSummaryTab}
                  />
                  <br />
                  <br />
                </TabPane>
              ) : null}
              {mrr.items?.length || mrr.summary?.length ? (
                <TabPane tab="Inward Data" key="Inward Data">
                  <div className={classes.previewMainHeading}>
                    Inward Tables
                  </div>
                  <br />
                  <Table
                    bordered
                    size="small"
                    columns={inwardItemsColumns}
                    dataSource={mrr.items}
                    rowKey={rowKeyItems}
                    pagination={false}
                    title={() => 'Items'}
                    scroll={scrollSummaryTab}
                  />
                  <br />
                  <Table
                    bordered
                    size="small"
                    columns={inwardSummaryColumns}
                    dataSource={mrr.summary}
                    rowKey={rowKeyItemsSummary}
                    pagination={false}
                    title={() => 'Items Summary'}
                    scroll={scrollSummaryTab}
                  />
                  <br />
                  <br />
                </TabPane>
              ) : null}
              {qcReport.items?.length || qcReport.summary?.length ? (
                <TabPane tab="QC Report Data" key="QC Report Data">
                  <div className={classes.previewMainHeading}>
                    Quality Check Tables
                  </div>
                  <br />
                  <Table
                    bordered
                    size="small"
                    columns={qcItemsColumns}
                    dataSource={qcReport.items}
                    rowKey={rowKeyQCItems}
                    pagination={false}
                    title={() => 'Items'}
                    scroll={scrollSummaryTab}
                  />
                  <br />
                  <Table
                    bordered
                    size="small"
                    columns={qcSummaryColumns}
                    dataSource={qcReport.summary}
                    rowKey={rowKeyQCItemsSummary}
                    pagination={false}
                    title={() => 'Items Summary'}
                    scroll={scrollSummaryTab}
                  />
                  <br />
                  <br />
                </TabPane>
              ) : null}
              {issuedItemsOfJobchart.fabrics?.length ||
              issuedItemsOfJobchart.accessories?.length ||
              issuedItemsOfJobchart.styles?.length ? (
                <TabPane tab="Issue Receive Data" key="Issue Receive Data">
                  <div className={classes.previewMainHeading}>
                    Issue-Receive Tables
                  </div>
                  {issuedItemsOfJobchart.fabrics?.length ? (
                    <>
                      <br />
                      <Table
                        bordered
                        size="small"
                        columns={issueFabricColumns}
                        dataSource={issuedItemsOfJobchart.fabrics}
                        rowKey={rowKeyFabrics}
                        pagination={false}
                        title={() => 'Fabrics'}
                        scroll={scrollSummaryTab}
                      />
                    </>
                  ) : null}
                  {issuedItemsOfJobchart.accessories?.length ? (
                    <>
                      <br />
                      <Table
                        bordered
                        size="small"
                        columns={issueAccessoryColumns}
                        dataSource={issuedItemsOfJobchart.accessories}
                        rowKey={rowKeyAccessories}
                        pagination={false}
                        title={() => 'Accessories'}
                        scroll={scrollSummaryTab}
                      />
                    </>
                  ) : null}
                  {issuedItemsOfJobchart.styles?.length ? (
                    <>
                      {' '}
                      <br />
                      <Table
                        bordered
                        size="small"
                        columns={issueStyleColumns}
                        dataSource={issuedItemsOfJobchart.styles}
                        rowKey={rowKeyStyles}
                        pagination={false}
                        title={() => 'Styles'}
                        scroll={scrollSummaryTab}
                      />{' '}
                    </>
                  ) : null}
                </TabPane>
              ) : null}
              {oldDepartmentTrackingOfJobchart.jobchartFabrics?.length ||
              oldDepartmentTrackingOfJobchart.jobchartStyles?.length ? (
                <TabPane tab="Tracking" key="Tracking">
                  <div className={classes.previewMainHeading}>Fabrics</div>
                  <br />
                  <Table
                    bordered
                    size="small"
                    columns={fabricsTracking.columns}
                    dataSource={oldDepartmentTrackingOfJobchart.jobchartFabrics}
                    rowKey={el =>
                      `p${el.productId}f${el.fabricId}b${el.baseColorId}p${el.printColorId}`
                    }
                    pagination={false}
                    title={() => 'Fabrics'}
                    scroll={fabricsTracking.scroll}
                  />
                  <br />
                  <Table
                    bordered
                    size="small"
                    columns={styleTracking.columns}
                    dataSource={oldDepartmentTrackingOfJobchart.jobchartStyles}
                    rowKey={el =>
                      `p${el.productId}f${el.styleId}b${el.baseColorId}p${el.printColorId}e${el.embroideryColorId}a${el.acidWashColorId}`
                    }
                    pagination={false}
                    title={() => 'Styles'}
                    scroll={styleTracking.scroll}
                  />
                  <br />
                  <br />
                </TabPane>
              ) : null}
            </Tabs>
          </TabPane>

          <TabPane tab="Store" key="store">
            <Table
              bordered
              size="small"
              columns={fabricsStoreTab.columns}
              dataSource={departmentTrackingOfJobchart.jobchartFabrics}
              rowKey={rowKeyFabrics}
              pagination={false}
              scroll={fabricsStoreTab.scroll}
              title={() => 'Fabrics'}
            />
            <br />
            <Table
              bordered
              size="small"
              columns={accessoriesStoreTab.columns}
              dataSource={departmentTrackingOfJobchart.jobchartAccessories}
              rowKey={rowKeyAccessories}
              pagination={false}
              scroll={accessoriesStoreTab.scroll}
              title={() => 'Accessories'}
            />
            <br />
            <Table
              bordered
              size="small"
              columns={stylesStoreTab.columns}
              dataSource={departmentTrackingOfJobchart.jobchartStyles}
              rowKey={rowKeyStyles}
              pagination={false}
              scroll={stylesStoreTab.scroll}
              title={() => 'Styles'}
            />
            <br />
          </TabPane>
          {fabricsCuttingTab.showTable ||
          accessoriesCuttingTab.showTable ||
          stylesCuttingTab.showTable ? (
            <TabPane tab="Cutting" key="cutting">
              {fabricsCuttingTab.showTable ? (
                <>
                  <Table
                    bordered
                    size="small"
                    columns={fabricsCuttingTab.columns}
                    dataSource={departmentTrackingOfJobchart.jobchartFabrics}
                    rowKey={rowKeyFabrics}
                    pagination={false}
                    scroll={fabricsCuttingTab.scroll}
                    title={() => 'Fabrics'}
                  />
                  <br />
                </>
              ) : null}
              {accessoriesCuttingTab.showTable ? (
                <>
                  <Table
                    bordered
                    size="small"
                    columns={accessoriesCuttingTab.columns}
                    dataSource={
                      departmentTrackingOfJobchart.jobchartAccessories
                    }
                    rowKey={rowKeyAccessories}
                    pagination={false}
                    scroll={accessoriesCuttingTab.scroll}
                    title={() => 'Accessories'}
                  />
                  <br />
                </>
              ) : null}
              {stylesCuttingTab.showTable ? (
                <>
                  <Table
                    bordered
                    size="small"
                    columns={stylesCuttingTab.columns}
                    dataSource={departmentTrackingOfJobchart.jobchartStyles}
                    rowKey={rowKeyStyles}
                    pagination={false}
                    scroll={stylesCuttingTab.scroll}
                    title={() => 'Styles'}
                  />
                  <br />
                </>
              ) : null}
            </TabPane>
          ) : null}
          {fabricsWashingTab.showTable ||
          accessoriesWashingTab.showTable ||
          stylesWashingTab.showTable ? (
            <TabPane tab="Washing" key="washing">
              {fabricsWashingTab.showTable ? (
                <>
                  <Table
                    bordered
                    size="small"
                    columns={fabricsWashingTab.columns}
                    dataSource={departmentTrackingOfJobchart.jobchartFabrics}
                    rowKey={rowKeyFabrics}
                    pagination={false}
                    scroll={fabricsWashingTab.scroll}
                    title={() => 'Fabrics'}
                  />
                  <br />
                </>
              ) : null}
              {accessoriesWashingTab.showTable ? (
                <>
                  <Table
                    bordered
                    size="small"
                    columns={accessoriesWashingTab.columns}
                    dataSource={
                      departmentTrackingOfJobchart.jobchartAccessories
                    }
                    rowKey={rowKeyAccessories}
                    pagination={false}
                    scroll={accessoriesWashingTab.scroll}
                    title={() => 'Fabrics'}
                  />
                  <br />
                </>
              ) : null}
              {stylesWashingTab.showTable ? (
                <>
                  <Table
                    bordered
                    size="small"
                    columns={stylesWashingTab.columns}
                    dataSource={departmentTrackingOfJobchart.jobchartStyles}
                    rowKey={rowKeyStyles}
                    pagination={false}
                    scroll={stylesWashingTab.scroll}
                    title={() => 'Styles'}
                  />
                </>
              ) : null}
            </TabPane>
          ) : null}
          {fabricsStitchingTab.showTable ||
          accessoriesStitchingTab.showTable ||
          stylesStitchingTab.showTable ? (
            <TabPane tab="Stitching" key="stitching">
              {fabricsStitchingTab.showTable ? (
                <>
                  <Table
                    bordered
                    size="small"
                    columns={fabricsStitchingTab.columns}
                    dataSource={departmentTrackingOfJobchart.jobchartFabrics}
                    rowKey={rowKeyFabrics}
                    pagination={false}
                    scroll={fabricsStitchingTab.scroll}
                    title={() => 'Fabrics'}
                  />
                  <br />
                </>
              ) : null}
              {accessoriesStitchingTab.showTable ? (
                <>
                  <Table
                    bordered
                    size="small"
                    columns={accessoriesStitchingTab.columns}
                    dataSource={
                      departmentTrackingOfJobchart.jobchartAccessories
                    }
                    rowKey={rowKeyAccessories}
                    pagination={false}
                    scroll={accessoriesStitchingTab.scroll}
                    title={() => 'Accessories'}
                  />
                  <br />
                </>
              ) : null}
              {stylesStitchingTab.showTable ? (
                <>
                  <Table
                    bordered
                    size="small"
                    columns={stylesStitchingTab.columns}
                    dataSource={departmentTrackingOfJobchart.jobchartStyles}
                    rowKey={rowKeyStyles}
                    pagination={false}
                    scroll={stylesStitchingTab.scroll}
                    title={() => 'Styles'}
                  />
                </>
              ) : null}
            </TabPane>
          ) : null}
          {fabricsFinishingTab.showTable ||
          fabricsFinishingTab.showTable ||
          stylesFinishingTab.showTable ? (
            <TabPane tab="Finishing" key="finishing">
              {fabricsFinishingTab.showTable ? (
                <>
                  <Table
                    bordered
                    size="small"
                    columns={fabricsFinishingTab.columns}
                    dataSource={departmentTrackingOfJobchart.jobchartFabrics}
                    rowKey={rowKeyFabrics}
                    pagination={false}
                    scroll={fabricsFinishingTab.scroll}
                    title={() => 'Fabrics'}
                  />
                  <br />
                </>
              ) : null}
              {accessoriesFinishingTab.showTable ? (
                <>
                  <Table
                    bordered
                    size="small"
                    columns={accessoriesFinishingTab.columns}
                    dataSource={
                      departmentTrackingOfJobchart.jobchartAccessories
                    }
                    rowKey={rowKeyAccessories}
                    pagination={false}
                    scroll={accessoriesFinishingTab.scroll}
                    title={() => 'Accessories'}
                  />
                  <br />
                </>
              ) : null}
              {stylesFinishingTab.showTable ? (
                <>
                  <Table
                    bordered
                    size="small"
                    columns={stylesFinishingTab.columns}
                    dataSource={departmentTrackingOfJobchart.jobchartStyles}
                    rowKey={rowKeyStyles}
                    pagination={false}
                    scroll={stylesFinishingTab.scroll}
                    title={() => 'Styles'}
                  />
                </>
              ) : null}
            </TabPane>
          ) : null}
          {fabricsPackingTab.showTable ||
          fabricsPackingTab.showTable ||
          stylesPackingTab.showTable ? (
            <TabPane tab="Packing" key="packing">
              {fabricsPackingTab.showTable ? (
                <>
                  <Table
                    bordered
                    size="small"
                    columns={fabricsPackingTab.columns}
                    dataSource={departmentTrackingOfJobchart.jobchartFabrics}
                    rowKey={rowKeyFabrics}
                    pagination={false}
                    scroll={fabricsPackingTab.scroll}
                    title={() => 'Fabrics'}
                  />
                  <br />
                </>
              ) : null}
              {accessoriesPackingTab.showTable ? (
                <>
                  <Table
                    bordered
                    size="small"
                    columns={accessoriesPackingTab.columns}
                    dataSource={
                      departmentTrackingOfJobchart.jobchartAccessories
                    }
                    rowKey={rowKeyAccessories}
                    pagination={false}
                    scroll={accessoriesPackingTab.scroll}
                    title={() => 'Accessories'}
                  />
                  <br />
                </>
              ) : null}
              {stylesPackingTab.showTable ? (
                <>
                  <Table
                    bordered
                    size="small"
                    columns={stylesPackingTab.columns}
                    dataSource={departmentTrackingOfJobchart.jobchartStyles}
                    rowKey={rowKeyStyles}
                    pagination={false}
                    scroll={stylesPackingTab.scroll}
                    title={() => 'Styles'}
                  />
                </>
              ) : null}
            </TabPane>
          ) : null}
          {fabricsDispatchTab.showTable ||
          fabricsDispatchTab.showTable ||
          stylesDispatchTab.showTable ? (
            <TabPane tab="Dispatch" key="dispatch">
              {fabricsDispatchTab.showTable ? (
                <>
                  <Table
                    bordered
                    size="small"
                    columns={fabricsDispatchTab.columns}
                    dataSource={departmentTrackingOfJobchart.jobchartFabrics}
                    rowKey={rowKeyFabrics}
                    pagination={false}
                    scroll={fabricsDispatchTab.scroll}
                    title={() => 'Fabrics'}
                  />
                  <br />
                </>
              ) : null}
              {accessoriesDispatchTab.showTable ? (
                <>
                  <Table
                    bordered
                    size="small"
                    columns={accessoriesDispatchTab.columns}
                    dataSource={
                      departmentTrackingOfJobchart.jobchartAccessories
                    }
                    rowKey={rowKeyAccessories}
                    pagination={false}
                    scroll={accessoriesDispatchTab.scroll}
                    title={() => 'Accessories'}
                  />
                  <br />
                </>
              ) : null}
              {stylesDispatchTab.showTable ? (
                <>
                  <Table
                    bordered
                    size="small"
                    columns={stylesDispatchTab.columns}
                    dataSource={departmentTrackingOfJobchart.jobchartStyles}
                    rowKey={rowKeyStyles}
                    pagination={false}
                    scroll={stylesDispatchTab.scroll}
                    title={() => 'Styles'}
                  />
                </>
              ) : null}
            </TabPane>
          ) : null}
          {departmentTrackingOfJobchart.rpoProcesses.length ? (
            <TabPane tab="Fabric" key="fabricTab">
              <Tabs defaultActiveKey="dyeing" tabPosition="left">
                {departmentTrackingOfJobchart.rpoProcesses.map(process => (
                  <TabPane tab={startCase(process)} key={process}>
                    {departmentTrackingOfJobchart.fabricProcesses[process] &&
                    departmentTrackingOfJobchart.fabricProcesses[process]
                      .length ? (
                      <>
                        <Table
                          bordered
                          size="small"
                          columns={columnsFabricsFabricTab}
                          dataSource={
                            departmentTrackingOfJobchart.fabricProcesses[
                              process
                            ]
                          }
                          rowKey={rowKeyFabrics}
                          pagination={false}
                          scroll={{ x: 1900 }}
                          title={() => 'Fabrics'}
                        />
                        <br />
                      </>
                    ) : null}
                    {departmentTrackingOfJobchart.accessoryProcesses[process] &&
                    departmentTrackingOfJobchart.accessoryProcesses[process]
                      .length ? (
                      <>
                        <Table
                          bordered
                          size="small"
                          columns={columnsAccessoriesFabricTab}
                          dataSource={
                            departmentTrackingOfJobchart.accessoryProcesses[
                              process
                            ]
                          }
                          rowKey={rowKeyAccessories}
                          pagination={false}
                          scroll={{ x: 1600 }}
                          title={() => 'Accessories'}
                        />
                        <br />
                      </>
                    ) : null}
                    {departmentTrackingOfJobchart.styleProcesses[process] &&
                    departmentTrackingOfJobchart.styleProcesses[process]
                      .length ? (
                      <Table
                        bordered
                        size="small"
                        columns={columnsStylesFabricTab}
                        dataSource={
                          departmentTrackingOfJobchart.styleProcesses[process]
                        }
                        rowKey={rowKeyStyles}
                        pagination={false}
                        scroll={{ x: 2200 }}
                        title={() => 'Styles'}
                      />
                    ) : null}
                  </TabPane>
                ))}
              </Tabs>
            </TabPane>
          ) : null}
        </Tabs>
      </div>
    </>
  );
};

TrackingJobchart.propTypes = {
  classes: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
};

export default withStyles(styles)(TrackingJobchart);
